/**
 * PortfolioNewsFeed - News feed sourced from yFinance for portfolio holdings.
 *
 * Uses GET /api/v1/portfolio/news which aggregates yFinance news for top holdings.
 * Falls back to SPY/QQQ general market news if no portfolio uploaded.
 */

import { useNavigate } from 'react-router-dom';
import { Newspaper, Clock } from 'lucide-react';
import { TickerLogo } from '@/components/ui/TickerLogo';
import type { PortfolioNewsItem } from '@/hooks/usePortfolioNews';

interface PortfolioNewsFeedProps {
  news: PortfolioNewsItem[];
  hasPortfolio: boolean;
  isLoading: boolean;
}

function timeAgo(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const diff = Date.now() - new Date(dateStr).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  } catch {
    return '';
  }
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="animate-pulse space-y-2 p-2.5">
          <div className="h-3 bg-surface-light rounded w-3/4" />
          <div className="h-2 bg-surface-light rounded w-1/2" />
        </div>
      ))}
    </div>
  );
}

export function PortfolioNewsFeed({ news, hasPortfolio, isLoading }: PortfolioNewsFeedProps) {
  const navigate = useNavigate();

  const handleTickerClick = (ticker: string) => {
    navigate(`/dashboard/stock-analysis?ticker=${ticker}`);
  };

  return (
    <div className="rounded-xl border border-border bg-surface-light/30 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Newspaper className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-medium text-text-primary">
          {hasPortfolio ? 'Portfolio News' : 'Market News'}
        </h3>
      </div>

      {isLoading ? (
        <LoadingSkeleton />
      ) : news.length === 0 ? (
        <p className="text-xs text-text-muted text-center py-4">No news available</p>
      ) : (
        <div className="space-y-1 max-h-[360px] overflow-y-auto pr-1">
          {news.map((item, i) => (
            <a
              key={i}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block group p-2 -mx-1 rounded-lg hover:bg-surface-light/50 transition-colors"
            >
              <p className="text-xs font-medium text-text-primary leading-relaxed group-hover:text-primary transition-colors line-clamp-2">
                {item.title}
              </p>
              <div className="flex items-center gap-2 mt-1">
                {item.ticker && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleTickerClick(item.ticker);
                    }}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-mono font-medium hover:bg-primary/20 transition-colors"
                  >
                    <TickerLogo symbol={item.ticker} size="xs" />
                    {item.ticker}
                  </button>
                )}
                {item.publisher && (
                  <span className="text-[10px] text-text-muted">{item.publisher}</span>
                )}
                {item.published_date && (
                  <span className="flex items-center gap-0.5 text-[10px] text-text-muted">
                    <Clock className="w-2.5 h-2.5" />
                    {timeAgo(item.published_date)}
                  </span>
                )}
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
